import React from 'react';
import { ArrowRightIcon, UsersIcon } from 'lucide-react';
import { DeepLink, goToDeepLink } from '../utils/deepLinks';
import { trackCustom } from '../utils/tracking';
interface OptionCard {
  link: DeepLink;
  title: string;
  price: string;
  body: string;
  note?: string;
  cta: string;
  popular?: boolean;
}

/** Priced piece by piece in the builder: Essential + Full Lens System + hard case + 1-year protection. */
const BUILT_PIECE_BY_PIECE = '$64.80';
const cards: OptionCard[] = [{
  link: 'build',
  title: 'Build your own',
  price: 'From $29.95',
  body: 'Frame + the lens you pick + USB-C cable. Add only what you want.',
  cta: 'Build my X1'
}, {
  link: 'complete',
  title: 'Complete system',
  price: '$49.95',
  body: 'Everything in the box: 3 lenses, hard case, pouch, cloth, cable.',
  note: `Built piece by piece, the same kit costs ${BUILT_PIECE_BY_PIECE}.`,
  cta: 'Get the complete X1'
}, {
  link: 'trio',
  title: 'Buy 2, get the 3rd free',
  price: '$99.90 · $33.30 each',
  body: 'Lifetime replacement included. Split it with two coworkers.',
  cta: 'Get 3 for $99.90',
  popular: true
}];
function select(link: DeepLink) {
  return (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    trackCustom('hero_offer_selected', {
      option: link
    });
    goToDeepLink(link);
  };
}

/** Four ways in, right at the top. Each whole card is one tap to its section, preselected. */
export function HeroOfferSelector() {
  return <div className="border-b border-white/10 bg-ink-950" data-id="element-603" data-mp="3b1mixfoq1nad">
      <div className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 lg:px-8 lg:py-14" data-id="element-604" data-mp="3orzo5aa8anw0">
        <h2 className="grit-headline text-[clamp(2.25rem,7vw,4rem)] font-extrabold uppercase text-offwhite" data-id="element-605" data-mp="3ou2mchljbnbt">
          Your X1, <span className="text-grit-500" data-id="element-606" data-mp="2wboxh7i6fbov">your way.</span>
        </h2>
        <p className="mt-3 max-w-2xl text-[16px] text-steel-300 sm:text-lg" data-id="element-607" data-mp="2w9vp9nrx0jer">
          Look at the options below and pick the one that fits your work. One tap takes you straight there. Free U.S.
          shipping on every order.
        </p>

        <div className="mt-8 grid gap-4 lg:grid-cols-3" data-id="element-608" data-mp="1080q2goz8h3g">
          {cards.map(card => <a key={card.link} href={`#${card.link}`} onClick={select(card.link)} className={`group relative flex h-full flex-col p-5 transition-[background-color,border-color] duration-150 ease-grit focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:p-6 ${card.popular ? 'border-2 border-grit-500 bg-grit-500/[0.07] hover:bg-grit-500/[0.12]' : 'border-2 border-white/15 bg-ink-900 hover:border-white/35'}`} data-id="element-609" data-mp="14ziujeq7kzt6">
              {card.popular && <span className="absolute -top-3 left-5 bg-grit-500 px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-[0.2em] text-ink-950" data-id="element-610" data-mp="1xohcaaqf5zxy">
                  Most popular
                </span>}
              <span className="font-display text-2xl font-extrabold uppercase leading-none text-offwhite sm:text-3xl" data-id="element-611" data-mp="3m7tzqykous8h">
                {card.title}
              </span>
              <span className="mt-2 font-display text-2xl font-bold uppercase leading-none text-grit-500" data-id="element-612" data-mp="3dfa2a1g4utp8">{card.price}</span>
              <span className="mt-3 text-[15px] leading-relaxed text-steel-300" data-id="element-613" data-mp="38gytzv5hkr94">{card.body}</span>
              {card.note && <span className="mt-2 text-[13px] font-semibold text-steel-400" data-id="element-614" data-mp="33i5k0cperidp">{card.note}</span>}
              <span className="mt-auto block pt-5" data-id="element-615" data-mp="2ygy6dx0l3oz4">
                <span className={`flex items-center justify-center gap-2 px-5 py-3.5 font-display text-lg font-extrabold uppercase tracking-wide transition-[background-color,color] duration-150 ease-grit ${card.popular ? 'bg-grit-500 text-ink-950 group-hover:bg-grit-400' : 'border-2 border-grit-500 text-grit-500 group-hover:bg-grit-500 group-hover:text-ink-950'}`} data-id="element-616" data-mp="2ugecd2bb51h7">
                  {card.cta}
                  <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-617" data-mp="3lj9n1w4x8hac" />
                </span>
              </span>
            </a>)}
        </div>

        <a href="#crew" onClick={select('crew')} className="group mt-4 flex flex-col gap-3 border-2 border-white/15 bg-ink-900 px-5 py-4 transition-colors duration-150 ease-grit hover:border-white/35 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:flex-row sm:items-center sm:justify-between sm:px-6" data-id="element-618" data-mp="37jgz28ks4f62">
          <span className="flex items-start gap-3" data-id="element-619" data-mp="2ouabo891izdx">
            <UsersIcon className="mt-0.5 h-6 w-6 shrink-0 text-grit-500" aria-hidden="true" data-id="element-620" data-mp="2orfqgei7dajg" />
            <span data-id="element-621" data-mp="2sshquynuvsn0">
              <span className="block font-display text-xl font-extrabold uppercase leading-tight text-offwhite" data-id="element-622" data-mp="2evcpvd301jym">
                Buying for a crew?
              </span>
              <span className="mt-0.5 block text-[14px] text-steel-300" data-id="element-623" data-mp="2jw8r1ivyus0c">
                30 complete systems, $23.33 each, lifetime replacement on all.
              </span>
            </span>
          </span>
          <span className="inline-flex shrink-0 items-center gap-2 font-display text-lg font-extrabold uppercase tracking-wide text-grit-500" data-id="element-624" data-mp="1vbnl32jq2826">
            See the Crew Pack
            <ArrowRightIcon className="h-5 w-5 transition-transform duration-150 ease-grit group-hover:translate-x-0.5" aria-hidden="true" data-id="element-625" data-mp="255w1wsze4cki" />
          </span>
        </a>
      </div>
    </div>;
}